import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhanceTableHeaderTypes,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import Loader from 'src/components/Loader';
import ImageIcon from '@mui/icons-material/Image';
import AddImage from './add-image';
import { DataService } from 'src/services/data-service';
import DeleteIcon from '@mui/icons-material/Delete';
import { IConfirmation } from 'src/redux/confirmation/types';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'src/redux/hooks';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';

interface IImageListing {
  id: string;
  code: string;
  name: string;
  uploadedDate: string;
  url: string;
}

interface IImageListingResponse {
  id: string;
  code: string;
  name: string;
  uploadDate: string;
  url: string;
}

const ImageListing: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<Record<string, IImageListing>>();
  const [openAddImage, setOpenAddImage] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const headers: EnhanceTableHeaderTypes[] = [
    { id: 'name', label: 'Name', type: EnhancedTableColumnType.TEXT },
    {
      id: 'uploadedDate',
      label: 'Date Uploaded',
      type: EnhancedTableColumnType.DATETIME
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.Action,
      actions: [
        {
          onClick: (id) => PreviewImage(id),
          icon: <ImageIcon fontSize="small" />,
          label: 'View Image',
          hidden: (r) => false
        },
        {
          onClick: (id) => CopyImageLink(id),
          icon: <ContentCopyIcon fontSize="small" />,
          label: 'Copy Link',
          hidden: () => false
        },
        {
          onClick: (id) => DeleteImage(id),
          icon: <DeleteIcon fontSize="small" />,
          label: 'Delete Image',
          hidden: (r) => false
        }
      ]
    }
  ];

  const initialize = async () => {
    setLoading(true);
    const response = await DataService.get('api/media/all');
    if (response.ok) {
      const list: IImageListingResponse[] = await response.json();
      const result: Record<string, IImageListing> = {};
      list.forEach((c) => {
        result[c.id] = {
          id: c.id,
          code: c.code,
          name: c.name,
          uploadedDate: c.uploadDate,
          url: c.url
        };
      });
      setData(result);
      setLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const CopyImageLink = (id: string) => {
    const image = data ? data[id] : null;
    if (image) {
      navigator.clipboard.writeText(image.url);
      toast.success('Image link copied to clipboard!');
    }
  };

  const PreviewImage = (id: string) => {
    const image = data ? data[id] : null;
    if (image) {
      setPreviewImage(image.url);
    }
  };

  const DeleteImage = (id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to delete this image?',
      onSubmit: async () => {
        const result = await DataService.delete(
          `api/media/delete?id=${id}`,
          {}
        );
        if (result.ok) {
          initialize();
          toast.success('Image deleted successfully!');
        } else {
          toast.error('An error occurred while deleting the image.');
        }
      }
    };

    dispatch(OpenConfirmation(confirmation));
  };

  const actions: ITableMenuAction[] = [
    {
      key: 'addImage',
      title: 'Add Image',
      disabled: (selected: string[]) => selected.length > 0,
      onClick: () => {
        setOpenAddImage(true);
      }
    }
  ];

  if (!data || loading) {
    return <Loader size={75} />;
  }

  return (
      <div>
        <Helmet>
          <title>Image Listing</title>
        </Helmet>
        <MainPageTitle
          title="Image List"
          action={{
            title: 'Add Image',
            onClick: () => {
              if (!openAddImage) {
                setOpenAddImage(true);
              }
            }
          }}
        />
    
        <EnhancedTable
          title="Images"
          header={headers}
          data={data}
          actions={actions}
          defaultOrder="desc"
          defaultOrderBy="uploadedDate"
        />
    
        {openAddImage && (
          <AddImage
            onSuccess={() => {
              setOpenAddImage(false);
              initialize();
            }}
            onClose={() => {
              setOpenAddImage(false);
            }}
          />
        )}

      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)}>
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          {previewImage && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  width: '100%',
                  maxHeight: '500px',
                  marginBottom: '10px'
                }}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(previewImage);
                  toast.success('Image link copied to clipboard!');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Copy Link
              </button>
            </div>
          )}
        </DialogContent>
        <IconButton
          onClick={() => setPreviewImage(null)}
          style={{ position: 'absolute', top: 10, right: 10 }}
        >
          ✖
        </IconButton>
      </Dialog>
    </div>
  );
};

export default ImageListing;
